import DataItem from "shared/components/DataItem";
import { FILTERS } from "./colourFiltersModels";
import Button from "shared/components/Button";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { setFilters } from "features/krpano/details-slice";
import { useCallback } from "react";
import { SliderWrapper, DropdownWrapper, RadioWrapper } from "./components";
import { ZFilterPlugin } from "krpano";
import Accordion from "shared/components/Accordion";

interface ColourFiltersProps {}

const ColourFilters: React.FC<ColourFiltersProps> = () => {
  // Plugin, State, and Dispatch
  const plugin = window.krpano.plugin.getItem<ZFilterPlugin>("zfilter");
  // IDEA: This would require less error checking and may still be responsive
  // for number values ```krpano.tween(`plugin[zfilter].${property}`, value)```
  const dispatch = useAppDispatch();
  // Callbacks
  const onClickReset = useCallback(() => {
    let defaults: Parameters<typeof setFilters>[0] = {};
    FILTERS.flatMap((section) => section.filters).forEach((filter) => {
      defaults[filter.property] = filter.filterOptions
        .default as (typeof defaults)[typeof filter.property];
      // Set in krpano
      try {
        if (plugin) {
          // NOTE: This is dangerous and should be type-checked properly
          // TODO: Improve types here
          // @ts-ignore-next-line
          plugin[filter.property] = filter.filterOptions.default;
        }
      } catch (error) {
        console.error("Error setting dropdown value in plugin:", error);
      }
    });
    dispatch(setFilters(defaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, plugin?.loaded]);

  return (
    <div className="DisplayFlex FlexColumn LargeGap">
      {FILTERS.map((filterSection, filterSectionIndex) => (
        <Accordion
          key={`FILTER_SECTION_${filterSectionIndex}`}
          headerChildren={<h6 className="MutedText">{filterSection.label}</h6>}
          bodyChildren={filterSection.filters.map((filter, filterIndex) => (
            <DataItem
              key={`FILTER_CONTROL_${filterIndex}`}
              // OPTION 1
              // Include margin if the value is on the right
              // SEE: RIGHT_MARGIN_VALUE
              // left={filter.label}
              // middle={
              //   filter.filterOptions.type === "slider" && (
              //     <FilterValue property={filter.property} />
              //   )
              // }
              // right={buildFilterControl(filter)}
              // rightSize="Wide"
              //
              // OPTION 2
              left={filter.label}
              middle={buildFilterControl(filter)}
              right={
                filter.filterOptions.type === "slider" && (
                  <FilterValue property={filter.property} />
                )
              }
              rightSize="Narrow"
            />
          ))}
          additionalBodyClasses="DisplayFlex FlexColumn SmallGap HasInset"
          withArrow
          subLevel={1}
          // Show only first section open initially
          initialOpen={filterSectionIndex === 0}
          mountOnEnter
          // unmountOnExit
        />
      ))}
      <Button colour="Theme" additionalClassNames="Wide" onClick={onClickReset}>
        Reset
      </Button>
    </div>
  );
};

interface FilterValueProps {
  property: (typeof FILTERS)[number]["filters"][number]["property"];
}

const FilterValue: React.FC<FilterValueProps> = ({ property }) => {
  const value = useAppSelector(
    (state) => state.krpano.plugins.zfilter[property]
  );
  return <div className="FilterValue">{value}</div>;
};

/**
 * Build the different kinds of filter controls
 */
const buildFilterControl = (
  filter: (typeof FILTERS)[number]["filters"][number]
) => {
  switch (filter.filterOptions.type) {
    case "slider":
      return (
        <SliderWrapper
          property={
            filter.property as Parameters<typeof SliderWrapper>[0]["property"]
          }
          min={filter.filterOptions.min}
          max={filter.filterOptions.max}
          // default={filter.filterOptions.default}
          step={filter.filterOptions.step}
        />
      );
    case "dropdown":
      return (
        <DropdownWrapper
          property={
            filter.property as Parameters<typeof DropdownWrapper>[0]["property"]
          }
        />
      );
    case "radio":
      return (
        <RadioWrapper
          property={
            filter.property as Parameters<typeof RadioWrapper>[0]["property"]
          }
          trueState={filter.filterOptions.trueState}
          falseState={filter.filterOptions.falseState}
        />
      );
    default:
      return null;
  }
};

export default ColourFilters;
